import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

export const invalidateAllPatientQueries = async (
  client: QueryClient,
  patient_id: string,
) => {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['patients'] }),
    client.invalidateQueries({ queryKey: ['patient', patient_id] }),
    client.invalidateQueries({ queryKey: ['patientLocation', patient_id] }),
    client.invalidateQueries({ queryKey: ['patientProfile', patient_id] }),
    client.invalidateQueries({
      queryKey: ['patient', patient_id, 'insurance'],
    }),
    client.invalidateQueries({ queryKey: ['patientCareTeam', patient_id] }),
    client.invalidateQueries({ queryKey: ['patientCareTeamCaregiver'] }),
    client.invalidateQueries({
      queryKey: ['programStatusHistory', patient_id],
    }),
    client.invalidateQueries({ queryKey: ['patientCareTeamEmergencyContact'] }),
    client.invalidateQueries({ queryKey: ['patientCareNotes', patient_id] }),
    client.invalidateQueries({ queryKey: ['patientCareNote', patient_id] }),
    client.invalidateQueries({ queryKey: ['patientDocuments', patient_id] }),
    client.invalidateQueries({
      queryKey: ['patientContactInformation', patient_id],
    }),
    client.invalidateQueries({ queryKey: ['task'] }),
    client.invalidateQueries({ queryKey: ['assessment'] }),
  ]);
};

const createPatientRequest = async (
  props: paths['/patient']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/patient']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/patient']['post']['responses']['201']['content']['application/json']
    >
  >(`/patient`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreatePatientMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patient'],
    mutationFn: (
      props: paths['/patient']['post']['requestBody']['content']['application/json'],
    ) => createPatientRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['patients'] }),
  });

  return mutation;
};

const editPatientProfileRequest = async (
  props: paths['/patient/{patient_id}/profile']['patch']['requestBody']['content']['application/json'] & {
    patient_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/profile']['patch']['requestBody']['content']['application/json'],
    paths['/patient/{patient_id}/profile']['patch']['requestBody']['content']['application/json']
  >(`/patient/${props.patient_id}/profile`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPatientProfileMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPatientProfile'],
    mutationFn: (
      props: paths['/patient/{patient_id}/profile']['patch']['requestBody']['content']['application/json'],
    ) => editPatientProfileRequest({ ...props, patient_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({ queryKey: ['patientProfile', patient_id] }),
      ]),
  });

  return mutation;
};

const editPatientlocationRequest = async (
  props: paths['/patient/{patient_id}/location']['patch']['requestBody']['content']['application/json'] & {
    patient_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/location']['patch']['requestBody']['content']['application/json'],
    paths['/patient/{patient_id}/location']['patch']['requestBody']['content']['application/json']
  >(`/patient/${props.patient_id}/location`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPatientLocationMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPatientLocation'],
    mutationFn: (
      props: paths['/patient/{patient_id}/location']['patch']['requestBody']['content']['application/json'],
    ) => editPatientlocationRequest({ ...props, patient_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({ queryKey: ['patientLocation', patient_id] }),
        client.invalidateQueries({
          queryKey: ['patientContactInformation', patient_id],
        }),
      ]),
  });

  return mutation;
};

const editPatientContactInformationRequest = async (
  props: paths['/patient/{patient_id}/contact-information']['patch']['requestBody']['content']['application/json'] & {
    patient_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/contact-information']['patch']['requestBody']['content']['application/json'],
    paths['/patient/{patient_id}/contact-information']['patch']['requestBody']['content']['application/json']
  >(`/patient/${props.patient_id}/contact-information`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPatientContactInformationMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPatientContactInformation'],
    mutationFn: (
      props: paths['/patient/{patient_id}/contact-information']['patch']['requestBody']['content']['application/json'],
    ) => editPatientContactInformationRequest({ ...props, patient_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({
          queryKey: ['patientCareTeamCaregiver', patient_id],
        }),
        client.invalidateQueries({
          queryKey: ['patientContactInformation', patient_id],
        }),
      ]),
  });

  return mutation;
};

/////////////////////////// INSURANCES /////////////////////

const createPatientInsuranceRequest = async (
  props: paths['/patient/{patient_id}/insurances']['post']['requestBody']['content']['application/json'] & {
    patient_id: string;
  },
) => {
  const { patient_id, ...body } = props;
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/insurances']['post']['responses']['201']['content']['application/json']
  >(`/patient/${patient_id}/insurances`, body);
  const data = (await response).data;
  return data;
};

export const useCreatePatientInsuranceMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createPatientInsurance', patient_id],
    mutationFn: (
      props: paths['/patient/{patient_id}/insurances']['post']['requestBody']['content']['application/json'],
    ) => createPatientInsuranceRequest({ ...props, patient_id }),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ['patient', patient_id, 'insurance'],
      }),
  });

  return mutation;
};

const updatePatientInsuranceRequest = async (
  props: paths['/patient/{patient_id}/insurances/{insurance_id}']['patch']['requestBody']['content']['application/json'] & {
    patient_id: string;
    // this is the id of the Insurance entity in our system, not the legal insurance id
    id: string;
  },
) => {
  const { patient_id, id, ...body } = props;
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/insurances/{insurance_id}']['patch']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/insurances/${id}`, body);
  const data = (await response).data;
  return data;
};

export const useUpdatePatientInsuranceMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['updatePatientInsurance', patient_id],
    mutationFn: (
      props: paths['/patient/{patient_id}/insurances/{insurance_id}']['patch']['requestBody']['content']['application/json'] & {
        id: string;
      },
    ) => updatePatientInsuranceRequest({ ...props, patient_id }),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ['patient', patient_id, 'insurance'],
      }),
  });

  return mutation;
};

const deletePatientInsuranceRequest = async ({
  patient_id,
  insurance_id,
}: {
  patient_id: string;
  insurance_id: string;
}) => {
  const response = axiosInstance.delete(
    `/patient/${patient_id}/insurances/${insurance_id}`,
  );
  await response;
};

export const useDeletePatientInsuranceMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deletePatientInsurance', patient_id],
    mutationFn: ({ insurance_id }: { insurance_id: string }) =>
      deletePatientInsuranceRequest({ patient_id, insurance_id }),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ['patient', patient_id, 'insurance'],
      }),
  });

  return mutation;
};

/////////////////////////// CHECK INSURANCE STATUS /////////////////////

const checkPatientPrimaryInsuranceStatusRequest = async (
  props: paths['/patient/{patient_id}/insurance']['post']['requestBody']['content']['application/json'] & {
    patient_id: string;
  },
) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/insurance']['post']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/insurance`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCheckPatientPrimaryInsuranceStatusMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const mutation = useMutation({
    mutationKey: ['checkPatientPrimaryInsuranceStatus'],
    mutationFn: (
      props: paths['/patient/{patient_id}/insurance']['post']['requestBody']['content']['application/json'],
    ) => checkPatientPrimaryInsuranceStatusRequest({ ...props, patient_id }),
  });

  return mutation;
};

const editPatientAdditionalInformationRequest = async (
  props: paths['/patient/{patient_id}/additional-information']['patch']['requestBody']['content']['application/json'] & {
    patient_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/additional-information']['patch']['requestBody']['content']['application/json'],
    paths['/patient/{patient_id}/additional-information']['patch']['requestBody']['content']['application/json']
  >(`/patient/${props.patient_id}/additional-information`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPatientAdditionalInformationMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPatientAdditionalInformation'],
    mutationFn: (
      props: paths['/patient/{patient_id}/additional-information']['patch']['requestBody']['content']['application/json'],
    ) => editPatientAdditionalInformationRequest({ ...props, patient_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({
          queryKey: ['patientAdditionalInformation', patient_id],
        }),
      ]),
  });

  return mutation;
};

// ++++++++++++++++++ ADD CAREGIVER +++++++++++++++++++

const addCaregiverRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/caregiver']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/caregiver']['patch']['responses']['200']['content']
  >(`/patient/${patient_id}/caregiver`, props);
  const data = await response;

  return data;
};

export const useAddCaregiverMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addCaregiver'],
    mutationFn: (
      props: paths['/patient/{patient_id}/caregiver']['patch']['requestBody']['content']['application/json'],
    ) => addCaregiverRequest(patient_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCareTeam'],
        }),
        client.invalidateQueries({
          queryKey: ['patientCareTeamCaregiver'],
        }),
        client.invalidateQueries({
          queryKey: ['patientContactInformation'],
        }),
        client.invalidateQueries({ queryKey: ['task'] }),
      ]),
  });

  return mutation;
};

//++++++++++++++++ DELETE CAREGIVER ++++++++++++++++

const deleteCaregiverRequest = async (patient_id: string) => {
  const response = axiosInstance.delete(`/patient/${patient_id}/caregiver`);
  const data = await response;

  return data;
};

export const useDeleteCaregiverMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteCaregiver'],
    mutationFn: () => deleteCaregiverRequest(patient_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patientCareTeam'] }),
        client.invalidateQueries({ queryKey: ['patientCareTeamCaregiver'] }),
        client.invalidateQueries({ queryKey: ['patientContactInformation'] }),
        client.invalidateQueries({ queryKey: ['patient'] }),
        client.invalidateQueries({ queryKey: ['task'] }),
      ]),
  });

  return mutation;
};

//++++++++++++++++++++ ADD EMERGENCY CONTACT +++++++++++++++++++

const addEmergencyContactRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/emergency-contact']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/emergency-contact']['post']['responses']['200']['content']
  >(`/patient/${patient_id}/emergency-contact`, props);
  const data = await response;

  return data;
};

export const useAddEmergencyContactMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addEmergencyContact'],
    mutationFn: (
      props: paths['/patient/{patient_id}/emergency-contact']['post']['requestBody']['content']['application/json'],
    ) => addEmergencyContactRequest(patient_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCareTeam'],
        }),
        client.invalidateQueries({
          queryKey: ['patientCareTeamEmergencyContact'],
        }),
      ]),
  });

  return mutation;
};

//++++++++++++++++++++ EDIT EMERGENCY CONTACT +++++++++++++++++++

const editEmergencyContactRequest = async (
  patient_id: string,
  pec_id: string,
  props: paths['/patient/{patient_id}/emergency-contact/{pec_id}']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/emergency-contact/{pec_id}']['patch']['responses']['200']['content']
  >(`/patient/${patient_id}/emergency-contact/${pec_id}`, props);
  const data = await response;

  return data;
};

export const useEditEmergencyContactMutation = (
  patient_id: string,
  pec_id: string,
) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editEmergencyContact'],
    mutationFn: (
      props: paths['/patient/{patient_id}/emergency-contact/{pec_id}']['patch']['requestBody']['content']['application/json'],
    ) => editEmergencyContactRequest(patient_id, pec_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCareTeam'],
        }),
        client.invalidateQueries({
          queryKey: ['patientCareTeamEmergencyContact'],
        }),
      ]),
  });

  return mutation;
};

//++++++++++++++++ DELETE EMERGENCY CONTACT ++++++++++++++++

const deleteEmergencyRequest = async (patient_id: string, pec_id: string) => {
  const response = axiosInstance.delete(
    `/patient/${patient_id}/emergency-contact/${pec_id}`,
  );
  const data = await response;

  return data;
};

export const useDeleteEmergencyContactMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteEmergencyContact'],
    mutationFn: (pec_id: string) => deleteEmergencyRequest(patient_id, pec_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patientCareTeam'] }),
        client.invalidateQueries({
          queryKey: ['patientCareTeamEmergencyContact'],
        }),
      ]),
  });

  return mutation;
};

//++++++++++++++++++ UPDATE CARE TEAM ++++++++++++++++++++

const updateCareTeamRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/care-team']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/care-team']['patch']['responses']['200']['content']
  >(`/patient/${patient_id}/care-team`, props);
  const data = await response;

  return data;
};

export const useUpdateCareTeamMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addEmergencyContact'],
    mutationFn: (
      props: paths['/patient/{patient_id}/care-team']['patch']['requestBody']['content']['application/json'],
    ) => updateCareTeamRequest(patient_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCareTeam'],
        }),
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

//++++++++++++++++++++ EDIT CARE PLAN +++++++++++++++++++

const editCarePlanRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/care-plan']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/care-plan']['patch']['responses']['200']['content']
  >(`/patient/${patient_id}/care-plan`, props);
  const data = await response;

  return data;
};

export const useEditCarePlanMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editCarePlan'],
    mutationFn: (
      props: paths['/patient/{patient_id}/care-plan']['patch']['requestBody']['content']['application/json'],
    ) => editCarePlanRequest(patient_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCarePlan'],
        }),
        client.invalidateQueries({
          queryKey: ['assessment', patient_id],
        }),
      ]),
  });

  return mutation;
};

//++++++++++++++++++++ ADD CARE NOTE +++++++++++++++++++

const addCareNoteRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/care-note']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/care-note']['post']['responses']['201']['content']
  >(`/patient/${patient_id}/care-note`, props);
  const data = await response;

  return data;
};

export const useAddCareNoteMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addPatientCareNote'],
    mutationFn: (
      props: paths['/patient/{patient_id}/care-note']['post']['requestBody']['content']['application/json'],
    ) => addCareNoteRequest(patient_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCareNotes'],
        }),
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

//++++++++++++++++++++ EDIT CARE NOTE +++++++++++++++++++

const editCareNoteRequest = async (
  patient_id: string,
  care_note_id: string,
  props: paths['/patient/{patient_id}/care-note/{care_note_id}']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/care-note/{care_note_id}']['patch']['responses']['200']['content']
  >(`/patient/${patient_id}/care-note/${care_note_id}`, props);
  const data = await response;

  return data;
};

export const useEditCareNoteMutation = (
  patient_id: string,
  care_note_id: string,
) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPatientCareNote'],
    mutationFn: (
      props: paths['/patient/{patient_id}/care-note/{care_note_id}']['patch']['requestBody']['content']['application/json'],
    ) => editCareNoteRequest(patient_id, care_note_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCareNotes'],
        }),
        client.invalidateQueries({
          queryKey: ['patientCareNote'],
        }),
        client.invalidateQueries({
          queryKey: ['activityLog'],
        }),
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

export const patientGenerateCarePlanPDFRequest = async ({
  patient_id,
  all_notes,
}: {
  patient_id?: string;
  all_notes: boolean;
}) => {
  const response = await axiosInstance.get(
    `/patient/${patient_id}/care-plan/pdf`,
    {
      params: {
        ...(all_notes && { all_notes }),
      },
      headers: {
        'Content-type': 'application/pdf',
      },
      responseType: 'blob',
    },
  );

  const data = (await response).data;

  const blob = new Blob([data], { type: 'application/pdf' });

  return blob;
};

export const usePatientGenerateCarePlanPDF = (patient_id?: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientGenerateCarePlanPDF'],
    mutationFn: ({ all_notes }: { all_notes: boolean }) =>
      patientGenerateCarePlanPDFRequest({ patient_id, all_notes }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientCarePlanPDF'],
        }),
      ]),
  });

  return mutation;
};

// +++++++++++++++++ Edit summary notes +++++++++++++++

const updateSummaryNotesRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/notes']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/notes']['patch']['responses']['200']['content']
  >(`/patient/${patient_id}/notes`, props);
  const data = await response;

  return data;
};

export const useUpdateSummaryNotesMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editSummaryNotes'],
    mutationFn: (
      props: paths['/patient/{patient_id}/notes']['patch']['requestBody']['content']['application/json'],
    ) => updateSummaryNotesRequest(patient_id, props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

// +++++++++++++++++ Download  Patients Care Plan PDF+++++++++++++++

export const patientCarePlanPDFDownloadRequest = async (
  own_patients_only: boolean,
) => {
  const response = await axiosInstance.get(`/patient/care-plans/pdf`, {
    headers: {
      'Content-type': 'application/pdf',
    },
    responseType: 'blob',
    params: {
      own_patients_only,
    },
  });

  const data = (await response).data;

  return data;
};

export const usePatientCarePlanPDFDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['patientCarePlanPDFDownload'],
    mutationFn: ({ own_patients_only }: { own_patients_only: boolean }) =>
      patientCarePlanPDFDownloadRequest(own_patients_only),
  });

  return mutation;
};

// ---------------- memory connect token ----------------

const getMemoryConnectTokenRequest = async (patient_id: string) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/mc2-token']['post']['responses']['201']['content']['application/json']
  >(`/patient/${patient_id}/mc2-token`);
  const data = await response;

  return data;
};

export const useGetMemoryConnectTokenMutation = (patient_id: string) => {
  const mutation = useMutation({
    mutationKey: ['memoryConnectToken'],
    mutationFn: () => getMemoryConnectTokenRequest(patient_id),
  });

  return mutation;
};

const unlockLockedPatientRequest = async (patient_id: string) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/unlock-request']['post']['responses']['200']['content']
  >(`/patient/${patient_id}/unlock-request`);
  const data = await response;

  return data;
};

export const useLockedPatientUnlockMutation = (patient_id: string) => {
  const mutation = useMutation({
    mutationKey: ['unlockLockedPatient'],
    mutationFn: () => unlockLockedPatientRequest(patient_id),
  });

  return mutation;
};

const patientLockRequest = async (patient_id: string) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/lock']['post']['responses']['200']['content']
  >(`/patient/${patient_id}/lock`);
  const data = await response;

  return data;
};

export const usePatientLockMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientLock'],
    mutationFn: () => patientLockRequest(patient_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patients'],
        }),
      ]),
    onError: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

const replyToPatientUnlockRequest = async (
  patient_id: string,
  approved: boolean,
) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/unlock-request/reply']['post']['responses']['200']['content']
  >(`/patient/${patient_id}/unlock-request/reply`, { approved });
  const data = await response;

  return data;
};

export const useReplyUnlockPatientRequestMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['replyUnlockLockedPatientRequest'],
    mutationFn: ({ approve }: { approve: boolean }) =>
      replyToPatientUnlockRequest(patient_id, approve),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
        client.invalidateQueries({
          queryKey: ['task'],
        }),
      ]),
  });

  return mutation;
};

export const patientDocumentUploadRequest = async (
  patient_id: string,
  file: FormData,
) => {
  const response = await axiosInstance.post(
    `/patient/${patient_id}/document/upload`,
    file,
    {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    },
  );

  const data = (await response).data;

  return data;
};

export const usePatientDocumentUploadMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientDocumentUpload'],
    mutationFn: ({ file }: { file: FormData }) =>
      patientDocumentUploadRequest(patient_id, file),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patientDocuments', patient_id],
        }),
        client.invalidateQueries({
          queryKey: ['task'],
        }),
      ]),
  });

  return mutation;
};

export const patientDocumentDownloadRequest = async (document_id: string) => {
  const response = await axiosInstance.get(
    `/patient/document/${document_id}/download`,
    {
      headers: {
        'Content-type': 'multipart/form-data',
      },
      responseType: 'blob',
    },
  );

  const data = (await response).data;

  return data;
};

export const usePatientDocumentDownloadMutation = () => {
  const mutation = useMutation({
    mutationKey: ['patientDocumentDownload'],
    mutationFn: (document_id: string) =>
      patientDocumentDownloadRequest(document_id),
  });

  return mutation;
};

const deletePatientDocumentRequest = async (document_id: string) => {
  const response = axiosInstance.delete(`/patient/document/${document_id}`);
  const data = await response;

  return data;
};

export const useDeletePatientDocumentMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deletePatientDocument'],
    mutationFn: (document_id: string) =>
      deletePatientDocumentRequest(document_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patientDocuments'] }),
        client.invalidateQueries({ queryKey: ['task'] }),
      ]),
  });

  return mutation;
};

interface IEnrollPatientProps {
  patient_id: string;
  program_id: string;
}

const enrollPatientRequest = async ({
  patient_id,
  program_id,
}: IEnrollPatientProps) => {
  const response = axiosInstance.post<
    paths['/program/{program_id}/{patient_id}/enroll']['post']['requestBody']
  >(`/program/${program_id}/${patient_id}/enroll`);
  const data = await response;

  return data;
};

export const useEnrollPatientMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['enrollPatient', patient_id],
    mutationFn: (props: IEnrollPatientProps) => enrollPatientRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

////////// Clinic caption ///////////////
const editPatientClinicalCaptionRequest = async ({
  patient_id,
  clinical_caption,
}: {
  clinical_caption: string;
  patient_id: string;
}) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/clinical-caption']['patch']['requestBody']['content']['application/json']
  >(`/patient/${patient_id}/clinical-caption`, {
    clinical_caption,
  });
  const data = await response;

  return data;
};

export const useEditPatientClinicalCaptionMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientClinicalCaption', patient_id],
    mutationFn: (clinical_caption: string) =>
      editPatientClinicalCaptionRequest({ clinical_caption, patient_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
      ]),
  });

  return mutation;
};

///////// PATIENT GOALS ///////////

const createPatientGoalRequest = async (
  props: paths['/patient/{patient_id}/goal']['post']['requestBody']['content']['application/json'],
  patient_id: string,
) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/goal']['post']['requestBody']['content']['application/json']
  >(`/patient/${patient_id}/goal`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreatePatientGoalMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientGoal'],
    mutationFn: ({
      props,
    }: {
      props: paths['/patient/{patient_id}/goal']['post']['requestBody']['content']['application/json'];
    }) => createPatientGoalRequest(props, patient_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({ queryKey: ['patientsGoals', patient_id] }),
      ]),
  });

  return mutation;
};

const editPatientGoalRequest = async (
  props: paths['/patient/{patient_id}/goal/{goal_id}']['patch']['requestBody']['content']['application/json'],
  patient_id: string,
  goal_id: string,
) => {
  const response = axiosInstance.patch<
    paths['/patient/{patient_id}/goal/{goal_id}']['patch']['requestBody']['content']['application/json']
  >(`/patient/${patient_id}/goal/${goal_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPatientGoalMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientGoal'],
    mutationFn: ({
      goal_id,
      props,
    }: {
      goal_id: string;
      props: paths['/patient/{patient_id}/goal/{goal_id}']['patch']['requestBody']['content']['application/json'];
    }) => editPatientGoalRequest(props, patient_id, goal_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({ queryKey: ['patientsGoals', patient_id] }),
      ]),
  });

  return mutation;
};

const deletePatientGoalRequest = async (
  patient_id: string,
  goal_id: string,
) => {
  const response = axiosInstance.delete(
    `/patient/${patient_id}/goal/${goal_id}`,
  );
  const data = await response;

  return data;
};

export const useDeletePatientGoalMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientGoal'],
    mutationFn: ({ goal_id }: { goal_id: string }) =>
      deletePatientGoalRequest(patient_id, goal_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({ queryKey: ['patientsGoals', patient_id] }),
      ]),
  });

  return mutation;
};

const completePatientGoalRequest = async (
  patient_id: string,
  goal_id: string,
) => {
  const response = axiosInstance.post(
    `/patient/${patient_id}/goal/${goal_id}/complete`,
  );
  const data = await response;

  return data;
};

export const useCompletePatientGoalMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patientGoal'],
    mutationFn: ({ goal_id }: { goal_id: string }) =>
      completePatientGoalRequest(patient_id, goal_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({ queryKey: ['patientsGoals', patient_id] }),
      ]),
  });

  return mutation;
};

const logCareCoordinationRequest = async (patient_id: string) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/care-coordination-task']['post']['parameters']['query'],
    AxiosResponse<
      paths['/patient/{patient_id}/care-coordination-task']['post']['responses']['201']['content']['application/json']
    >
  >(`/patient/${patient_id}/care-coordination-task`);
  const data = await response;

  return data;
};

export const useLogCareCoordinationMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patient'],
    mutationFn: () => logCareCoordinationRequest(patient_id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['patient', patient_id] }),
  });

  return mutation;
};

const updatePatientTablePreferencesRequest = async (
  props: Partial<
    paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']
  >,
) => {
  const response = axiosInstance.patch<
    paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']
  >(`/preferences/patient-table`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useUpdatePatientTablePreferencesMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patient', 'table', 'preferences', 'edit'],
    mutationFn: (
      props: Partial<
        paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']
      >,
    ) => updatePatientTablePreferencesRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({
          queryKey: ['patient', 'table', 'preferences'],
        }),
      ]),
  });

  return mutation;
};

const deletePatientTablePreferencesRequest = async () => {
  const response = axiosInstance.delete(`/preferences/patient-table`);
  const data = await response;

  return data;
};

export const useDeletePatientTablePreferencesMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patient', 'table', 'preferences', 'delete'],
    mutationFn: () => deletePatientTablePreferencesRequest(),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({
          queryKey: ['patient', 'table', 'preferences'],
        }),
      ]),
  });

  return mutation;
};
